import { DateTime, IANAZone } from "luxon";

const TZ_OFFSET_REGEX = /[+-]\d{2}:\d{2}$/;
const LOCAL_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const LOCAL_TIME_REGEX = /^\d{2}:\d{2}$/;

/**
 * Plain, framework-free datetime helpers built on luxon.
 *
 * This is the single implementation of the timezone conversions shared by the
 * server domain and the api-client. It returns PLAIN values only — no
 * branded/domain types live here. Callers that need brands (`UTCDateTimeString`,
 * `Timezone`, `LocalTimeString`, …) wrap these functions and apply the brand
 * themselves (the server in its domain value-objects, the api-client in its own
 * datetime brands for the web).
 *
 * Conventions:
 *  - UTC datetime: ISO 8601 with a `Z` suffix or offset (e.g.
 *    `"2026-01-04T08:00:00.000Z"`).
 *  - Local datetime: ISO 8601 WITHOUT a timezone suffix (e.g.
 *    `"2026-01-04T11:00:00"`).
 *  - Local date: `YYYY-MM-DD`. Local time: `HH:mm`.
 *  - Timezone: an IANA identifier (e.g. `"America/New_York"`).
 */

// ============================================================================
// VALIDATORS (plain predicates — used by callers to build branded schemas)
// ============================================================================

/** Whether `value` is a valid IANA timezone identifier. */
export function isValidTimezone(value: string): boolean {
	return IANAZone.isValidZone(value);
}

/** Whether `value` looks like a UTC datetime (has `Z` suffix or offset). */
export function isUtcDateTimeString(value: string): boolean {
	return value.includes("Z") || TZ_OFFSET_REGEX.test(value);
}

/** Whether `value` looks like a local datetime (no timezone suffix). */
export function isLocalDateTimeString(value: string): boolean {
	return !(value.includes("Z") || TZ_OFFSET_REGEX.test(value));
}

/** Whether `value` is a local date string (`YYYY-MM-DD`). */
export function isLocalDateString(value: string): boolean {
	return LOCAL_DATE_REGEX.test(value);
}

/** Whether `value` is a local time string (`HH:mm`). */
export function isLocalTimeString(value: string): boolean {
	return LOCAL_TIME_REGEX.test(value);
}

// ============================================================================
// CONVERSIONS (plain string in / plain string out)
// ============================================================================

function toUtcDateTime(utc: string | Date): DateTime {
	const dt =
		utc instanceof Date
			? DateTime.fromJSDate(utc, { zone: "utc" })
			: DateTime.fromISO(utc, { setZone: true }).toUTC();
	if (!dt.isValid) {
		throw new Error("Invalid UTC datetime");
	}
	return dt;
}

/**
 * Convert a UTC datetime to a local datetime string (no timezone suffix) in the
 * given IANA timezone. Format: `YYYY-MM-DDTHH:mm:ss`.
 */
export function utcToLocal(utc: string | Date, timezone: string): string {
	return toUtcDateTime(utc).setZone(timezone).toFormat("yyyy-MM-dd'T'HH:mm:ss");
}

/** Convert a UTC datetime to a date-only string (`YYYY-MM-DD`) in `timezone`. */
export function utcToLocalDate(utc: string | Date, timezone: string): string {
	const date = toUtcDateTime(utc).setZone(timezone).toISODate();
	if (!date) {
		throw new Error("Failed to format local date");
	}
	return date;
}

/** Convert a UTC datetime to a time-only string (`HH:mm`) in `timezone`. */
export function utcToLocalTime(utc: string | Date, timezone: string): string {
	return toUtcDateTime(utc).setZone(timezone).toFormat("HH:mm");
}

/** Convert a UTC datetime to its local date + time parts in `timezone`. */
export function utcToLocalParts(
	utc: string | Date,
	timezone: string
): { date: string; time: string } {
	return {
		date: utcToLocalDate(utc, timezone),
		time: utcToLocalTime(utc, timezone),
	};
}

/**
 * Convert a local date (`YYYY-MM-DD`) + time (`HH:mm`) in `timezone` to a UTC
 * datetime string for storage.
 */
export function localToUtc(
	date: string,
	time: string,
	timezone: string
): string {
	const dt = DateTime.fromFormat(`${date} ${time}`, "yyyy-MM-dd HH:mm", {
		zone: timezone,
	});
	if (!dt.isValid) {
		throw new Error("Invalid local datetime");
	}
	const iso = dt.toUTC().toISO({ suppressMilliseconds: false });
	if (!iso) {
		throw new Error("Failed to format UTC datetime");
	}
	return iso;
}

/**
 * Convert a local datetime string (no timezone suffix) in `timezone` to a UTC
 * datetime string for storage.
 */
export function localDateTimeToUtc(local: string, timezone: string): string {
	// Luxon can parse ISO without zone when a zone is provided.
	const dt = DateTime.fromISO(local, { zone: timezone });
	if (!dt.isValid) {
		throw new Error("Invalid local datetime");
	}
	const iso = dt.toUTC().toISO({ suppressMilliseconds: false });
	if (!iso) {
		throw new Error("Failed to format UTC datetime");
	}
	return iso;
}
